import React, { useState } from "react";
import { Modal, Table } from "react-bootstrap";

import moment from "moment";
import EmptyData from "../EmptyData";
import { toPesoAmount } from "../../helpers";
import PacmanWrapper from "./PacmanWrapper";
import useFetch from "../../hooks/useFetch";
import { isEmpty, isNil, isNull } from "lodash";
import { Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";

import PerfectScrollbar from "react-perfect-scrollbar";
import { DB_DATE_FORMAT, SOURCE_ADD_STR } from "../../constants";

export const CardList = ({
  params = {},
  header = "",
  mapFunction,
  className = "",
}) => {
  const reqObj = {
    url: "/dashboard/sales-by-payment/type",
    method: "post",
    body: { ...params },
  };

  const { data, isLoading } = useFetch(reqObj, {}, [params.excludeDeliveryFee]);
  const items = data || [];

  return (
    <>
      {isLoading || data === null ? (
        <PacmanWrapper />
      ) : (
        <div className={`analytics-card-list h-100 font-size-md ${className}`}>
          {header && <h4 className="pt-8 px-8">{header}</h4>}

          <div className="card-body">
            {items.length <= 0 ? (
              <EmptyData className="text-center mt-12" />
            ) : (
              <>
                <ul className="mt-2 p-0">{items.map(mapFunction)}</ul>

                {params.isTotalVisible && (
                  <h5 className="text-right border-top border-dark-75 w-75 font-weight-bold ml-auto px-4 pt-4">
                    {toPesoAmount(
                      items.reduce((total, cur) => total + cur.sales, 0),
                    )}
                  </h5>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export const CardListByReferrer = ({
  params = {},
  header = "",
  mapFunction,
  className = "",
  onSourceNameSubmit,
  stores,
  isBrand,
  data: parentData,
  isLoading: parentIsLoading,
  onFilterChange,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [options, setOptions] = useState([]);

  const reqObj = {
    url: "/dashboard/sales-by-referrer/type",
    method: "post",
    body: { ...params },
  };

  const { data, isLoading, refetch } = useFetch(reqObj, {}, [
    params.filter,
    params.excludeDeliveryFee,
  ]);
  const items = data ? data.data : [];
  const utm_sources = data ? data.utm_sources : [];

  if (
    isEmpty(options) &&
    !isLoading &&
    (!isEmpty(items) || !isEmpty(utm_sources))
  ) {
    const itemLabels = items
      .filter(
        (item) =>
          !isEmpty(item._id) && item._id !== "null" && !isNull(item._id),
      )
      .map((item) => item._id);

    const allSourceLabels = [...new Set([...itemLabels, ...utm_sources])];

    const new_options = allSourceLabels.map((label, idx) => ({
      id: idx,
      label: label,
    }));

    new_options.unshift({ id: "all", label: "All" });

    if (params.role === "brand_admin" || params.role === "store_admin") {
      new_options.push({ id: "new", label: SOURCE_ADD_STR });
    }
    setOptions(new_options);
  }

  const onClickFilter = (value) => {
    onFilterChange(value);
  };
  return (
    <>
      {isLoading || data === null ? (
        <PacmanWrapper />
      ) : (
        <div className={`analytics-card-list h-100 font-size-md ${className}`}>
          <div className="d-flex flex-row justify-content-space-between">
            {header && <h4 className="pt-8 pl-8">{header}</h4>}
            <Button
              variant="link"
              className="pt-4 pr-8"
              onClick={() => setOpenModal(true)}
            >
              Manage Sources
            </Button>
          </div>
          <div className="card-body">
            {items.length <= 0 ? (
              <EmptyData className="text-center mt-12" />
            ) : (
              <>
                <ul className="mt-2 p-0">
                  {groupData(data.data).map(mapFunction)}
                </ul>

                {params.isTotalVisible && (
                  <h5 className="text-right border-top border-dark-75 w-75 font-weight-bold ml-auto px-4 pt-4">
                    {toPesoAmount(
                      items.reduce((total, cur) => total + cur.sales, 0),
                    )}
                  </h5>
                )}
              </>
            )}
          </div>
          <SourceManagerModal
            show={openModal}
            isLoading={parentIsLoading}
            onHide={() => setOpenModal(false)}
            onSubmit={(sourceName, selected) =>
              onSourceNameSubmit(sourceName, selected, refetch)
            }
            stores={stores}
            isBrand={isBrand}
            sources={options}
            onClickFilter={onClickFilter}
            items={items}
            params={params}
          />
        </div>
      )}
    </>
  );

  function groupData(data) {
    if (!data) return [];

    const groupMap = new Map();

    for (const item of data) {
      let groupKey;
      if (isNil(item._id)) {
        groupKey = "pickup.ph";
      } else {
        if (item._id.includes("fbclid")) {
          groupKey = "https://facebook.com";
        } else if (
          item._id.includes("pickup.ph") ||
          item._id === "" ||
          item._id === "null"
        ) {
          groupKey = "pickup.ph";
        } else {
          groupMap.set(item, item);
          continue;
        }
      }

      if (groupMap.has(groupKey)) {
        const existing = groupMap.get(groupKey);
        existing.sales += item.sales;
        existing.instance += item.instance;
      } else {
        groupMap.set(groupKey, {
          _id: groupKey,
          sales: item.sales,
          instance: item.instance,
        });
      }
    }

    // Convert the Map back to an array and sort by sales in descending order
    const sortedData = Array.from(groupMap.values()).sort(
      (a, b) => b.sales - a.sales,
    );
    return sortedData;
  }
};

const SourceManagerModal = ({
  show = false,
  onHide = null,
  onSubmit = null,
  isLoading = false,
  stores = [],
  isBrand = false,
  sources = [],
  items = [],
  params = {},
}) => {
  const [sourceName, setSourceName] = useState("");
  const [selected, setSelected] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(sourceName, selected);
    setSourceName("");
    setSelected([]);
    setIsSubmitting(false);
    setShowAddForm(false);
  };

  const allSources = sources
    .filter((s) => s.label !== "All" && s.label !== SOURCE_ADD_STR)
    .map((s) => s.label);

  const tableData = allSources.map((source) => {
    const itemData = items.find((item) => item._id === source) || {};
    return {
      _id: source,
      instance: itemData.instance || 0,
      sales: itemData.sales || 0,
    };
  });

  const getDateRangeText = () => {
    const { dateRange = "day", date } = params;
    const isCurrent = date === moment().format(DB_DATE_FORMAT);
    const prefix = isCurrent ? "This" : "Last";

    if (dateRange === "week") return `${prefix} Week`;
    if (dateRange === "month") return `${prefix} Month`;
    if (dateRange === "year") return `${prefix} Year`;
    if (dateRange === "allTime") return "All-time";
    if (date === moment().subtract(1, "days").format(DB_DATE_FORMAT))
      return "Yesterday";

    return "Today";
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manage Sources</Modal.Title>
        <Button
          variant="primary"
          className="ml-auto"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          + Add New
        </Button>
      </Modal.Header>
      <Modal.Body>
        {showAddForm && (
          <div className="mb-4 p-4 border rounded">
            <h5>Add New Source Referrer</h5>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Source Name</label>
                <input
                  name="source_name"
                  className="form-control mb-2"
                  placeholder="Enter new campaign or source name"
                  type="text"
                  value={sourceName}
                  onChange={(e) =>
                    setSourceName(e.target.value.replace(/ /g, "_"))
                  }
                />
              </div>
              {isBrand && (
                <div className="form-group">
                  <label>Apply to stores:</label>
                  <Typeahead
                    id="store-selector"
                    placeholder={"Stores"}
                    options={stores}
                    onChange={(value) => setSelected(value)}
                    selected={selected}
                    multiple={true}
                  />
                </div>
              )}
              <div className="d-flex justify-content-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowAddForm(false)}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || sourceName.trim() === ""}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </div>
        )}
        <PerfectScrollbar style={{ maxHeight: "400px" }}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Source</th>
                <th>
                  No. of Orders <br />
                  <small>({getDateRangeText()})</small>
                </th>
                <th>Sales</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((item) => (
                <tr key={item._id}>
                  <td>{item._id}</td>
                  <td>{item.instance}</td>
                  <td>{toPesoAmount(item.sales)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </PerfectScrollbar>
      </Modal.Body>
    </Modal>
  );
};

export default CardList;
